import { type JSX, type ReactNode, useEffect, useRef, useState } from 'react';

import { oberflaeche as ladeOberflaeche } from '../api/verwaltung';
/*
 * NUR DER TYP (B8). Ein `import type` verschwindet beim Uebersetzen restlos —
 * eine Werteinfuhr aus @crm/kontrakte zoege zod in das Buendel, weil das Paket
 * CommonJS ist. Der Grund steht ausfuehrlich im Kontrakt selbst.
 */
import type { Bereichsgruppenkennung, Bereichskennung } from '@crm/kontrakte';

import { herkunftVon, type Benutzer, type Recht } from '../api/anmeldung';
import { Aufklapper, type Aufklappeintrag, eintrittOhnePfeil, type Schwebezeile } from './Aufklapper';
import { Schublade } from './Schublade';
import { Kachelzeichen, Sinnbild, type Kachelsinnbild, type NamenVon } from './Sinnbild';
import { useTelefon } from './telefon';
import { Titelkarte, type Titelkartenangabe } from './Titelkarte';

/**
 * Ein Bereich der Menüzeile und das Recht, das ihn sichtbar macht.
 *
 * Die Zuordnung ist Tabelle 7 des Pflichtenhefts, wortgetreu. Abschnitt 4.3
 * sagt dazu den entscheidenden Satz: "Die Gruppe waehlt nicht das Menue aus."
 * Es gibt keine gepflegte Menuedefinition je Rolle — jeder Punkt traegt sein
 * Recht, und angezeigt wird er, wenn der Benutzer es hat. Daraus folgt
 * dreierlei: Eine zusaetzlich vergebene Berechtigung erweitert das Menue
 * sofort, ein Benutzer mit zwei Gruppen bekommt die Vereinigung beider
 * (BEN-03), und eine Rechteaenderung wirkt beim naechsten Seitenaufruf ohne
 * neue Anmeldung (BEN-05).
 *
 * Das Menue ist eine Erleichterung, KEINE Sicherung: Die Rechtepruefung
 * findet serverseitig in jeder Maske statt (BEN-08).
 */
export type Bereich = {
  /**
   * Die Kennung aus @crm/kontrakte (B8, 11.09.2026).
   *
   * Sie ist keine freie Zeichenkette: Die Startseite springt mit demselben Wort
   * hierher, und bis diese Aufzaehlung entstand, war ein Tippfehler dort eine
   * Maske, die es nicht gibt.
   */
  readonly kennung: Bereichskennung;
  readonly beschriftung: string;
  /**
   * Das Sinnbild des Bereichs: Duotone auf seiner Gruppenseite (ÄA-41), Light
   * vor seinem Eintrag in der Menüzeile (Auftrag #25). Beide Gewichte müssen
   * erzeugt sein — sonst ist es ein Typfehler (symbole-erzeugen.mjs).
   */
  readonly sinnbild: NamenVon<'duotone'> & NamenVon<'light'>;
  /**
   * EIN SATZ, DER SAGT, WAS MAN HIER TUT (12.09.2026).
   *
   * Er steht auf der Bereichsseite unter der Beschriftung. Auftraggeber:
   * „dort koennten dann auch kurze infos zu den einzelnen punkten stehen."
   *
   * PFLICHTFELD, und das ist der Sinn: Ein Bereich ohne Erklaerung ist ein Wort
   * ohne Auskunft — „Belege und Vorlagen" sagt niemandem, dass dort die
   * Stornosaetze stehen. Als optionales Feld haette der naechste neue Bereich
   * keine, und niemandem waere es aufgefallen.
   *
   * KEIN WERBETEXT UND KEINE ANFORDERUNGSNUMMER: Was man hier tut, in einem
   * Satz, in der Sprache des Hauses.
   */
  readonly erklaerung: string;
  /** `undefined` = immer sichtbar. Sonst: Objekt und eine der Aktionen. */
  readonly verlangt?: {
    readonly objekt: string;
    readonly aktionen: readonly string[];
  };
};

/**
 * Was der Seitenkopf einer Maske traegt (Tab. 7 i.d.F. ÄA-41).
 *
 * Die Aufteilung ist die der Vorlage: Der KICKER ordnet ein — "Kunde ·
 * K-11840 · Geschaeftskunde" —, der TITEL nennt den Namen, die UNTERZEILE
 * den Zusammenhang. Wer alles in den Titel schreibt, bekommt eine
 * sechsundzwanzig Punkt grosse Zeile, die umbricht.
 */
export type Kopf = {
  readonly titel: string;
  readonly kicker?: string | undefined;
  readonly unterzeile?: string | undefined;
  readonly kennzeichen: JSX.Element | undefined;
  /** Die Sinnbildkachel einer Akte (ÄA-41). */
  readonly sinnbild?: Kachelsinnbild | undefined;
  /** Die Kennzahlenleiste einer Akte (ÄA-41). */
  readonly kennzahlen?: JSX.Element | undefined;
  /** Handlungen der Maske im Seitenkopf, vor der Haupthandlung (ÄA-41). */
  readonly handlungen?: JSX.Element | undefined;
  /** Eine Karte am Titel, die beim Überfahren aufklappt — die Kontaktdaten der Kundenakte (CRM-56). */
  readonly titelkarte?: Titelkartenangabe | undefined;
  /**
   * Der niedrige Kopf (Auftrag CRM-95): kleinere Kachel und Titelstufe, eine
   * enge Kennzahlenleiste, weniger Luft. Bisher nur die Buchungsakte.
   */
  readonly kompakt?: boolean | undefined;
};

/**
 * Eine Gruppe der Menüzeile (2.10, Vorlage; seit ÄA-41 oben statt links).
 *
 * ============================================================================
 * SEIT DEM 12.09.2026 IST DIE GRUPPE SELBST EIN MENUEPUNKT
 * ============================================================================
 *
 * Auftraggeber: „mittlerweile ist das linke menue so voll und fuehrt zum
 * scrollen, dass ich ueberlege, dass die ueberschriften die menuepunkte werden
 * und eine seite aufrufen, in der die anderen punkte aufrufbar werden. dort
 * koennten dann auch kurze infos zu den einzelnen punkten stehen."
 *
 * Die Spalte fuehrte acht Ueberschriften und dreiundzwanzig Punkte — einunddreissig
 * Zeilen, dazu Marke und Benutzer. Auf einem Bildschirm mit 900 Punkten Hoehe
 * rollt das, und was unten liegt, findet man nur, wenn man es schon kennt.
 *
 * WAS JETZT IN DER SPALTE STEHT: die acht Gruppen. Die Punkte einer Gruppe
 * erscheinen darunter, sobald man in ihr arbeitet — und nur dort. Damit stehen
 * nie mehr als acht plus die Punkte EINER Gruppe da; im schlimmsten Fall
 * (System) sind es fuenfzehn Zeilen statt einunddreissig.
 *
 * ============================================================================
 * WARUM DIE PUNKTE NICHT GANZ VERSCHWINDEN
 * ============================================================================
 *
 * Der Vorschlag liest sich auch strenger: nur Gruppen in der Spalte, alles
 * andere ueber die Bereichsseite. Das waere kuerzer und im Alltag teurer — der
 * Empfang oeffnet „Buchungen" fuenfzigmal am Tag, und jeder Weg dorthin ginge
 * ueber eine Zwischenseite.
 *
 * Die offene Gruppe behaelt ihre Punkte deshalb. Der Gewinn an Ruhe bleibt
 * (sieben von acht Gruppen sind eingeklappt), der Weg zur taeglichen Arbeit
 * bleibt EIN Klick.
 *
 * ============================================================================
 * EINE GRUPPE MIT EINEM EINZIGEN PUNKT HAT KEINE BEREICHSSEITE
 * ============================================================================
 *
 * „Uebersicht" fuehrt auf das Dashboard, „Faktura" auf die Rechnungen — direkt.
 * Eine Seite, die eine einzige Karte zeigt, ist ein Klick, der nichts erklaert.
 * Entschieden wird das an der Zahl der SICHTBAREN Punkte: Wer nur eine der drei
 * Stammdatenmasken sehen darf, kommt auch dort direkt hin.
 *
 * ============================================================================
 * DIE GRUPPE WAEHLT NICHT DAS MENUE AUS
 * ============================================================================
 *
 * Abschnitt 4.3 gilt unveraendert: Jeder Punkt traegt sein Recht, und
 * angezeigt wird er, wenn der Benutzer es hat. Die Gruppe ist keine
 * Rechteklasse — sie erscheint nur, wenn unter ihr ueberhaupt etwas steht. Eine
 * leere Gruppe waere die Auskunft „hier gibt es etwas, das du nicht siehst",
 * und die gibt das Menue nicht.
 */
export type Bereichsgruppe = {
  /** Das Sprungziel der Gruppe, aus @crm/kontrakte. */
  readonly kennung: Bereichsgruppenkennung;
  readonly beschriftung: string;
  /**
   * Was die Gruppe zusammenhaelt — ein Satz, ueber den Karten ihrer Seite.
   *
   * Sie beantwortet die Frage, die eine Ueberschrift offen laesst: Warum stehen
   * gerade DIESE Punkte beieinander? „Betrieb" und „Stammdaten" sind sonst zwei
   * Woerter, zwischen denen man raet.
   */
  readonly erklaerung: string;
  readonly bereiche: readonly Bereich[];
};

export const BEREICHSGRUPPEN: readonly Bereichsgruppe[] = [
  /*
   * "UEBERSICHT" STEHT DA, WO BIS EBEN DIE ROLLE STAND.
   *
   * Die Rolle hing unter der Marke und las sich wie die erste Ueberschrift
   * des Menues — sie ist aber keine: Sie sagt nichts darueber, was darunter
   * kommt. Seit es echte Gruppenueberschriften gibt, war sie die einzige, die
   * nicht haelt, was ihre Form verspricht.
   *
   * Sie ist damit nicht verloren: Sie steht unten beim Benutzer, wo sie
   * hingehoert — die Rolle gehoert zur Person und nicht zur Gliederung. Dort
   * stand sie ohnehin schon.
   */
  {
    kennung: 'uebersicht',
    beschriftung: 'Übersicht',
    erklaerung: 'Der Einstieg in den Tag.',
    bereiche: [
      {
        kennung: 'dashboard',
        sinnbild: 'squares-four',
        beschriftung: 'Dashboard',
        erklaerung:
          'Was heute ansteht: Kennzahlen, ablaufende Fristen und Aufgaben, die auf '
          + 'eine Entscheidung warten.',
      },
    ],
  },
  /*
   * DIE POSTFAECHER STEHEN FUER SICH (Auftrag CRM-185, Ulf am 10.10.2026:
   * „Postfächer als eigenen Menüpunkt, er wird für den vertrieb und service
   * benötigt.").
   *
   * Bis dahin der letzte Punkt im Service (CRM-176) — wer im Vertrieb arbeitet,
   * sucht ihn dort nicht, obwohl er `support:sehen` traegt. Direkt nach der
   * Uebersicht, weil der Posteingang ein Einstieg in den Tag ist. Eine Gruppe
   * mit einem Punkt wie die Faktura: Der Klick fuehrt direkt hinein.
   *
   * Die Bereichskennung blieb `posteingang` und die Adresse `/postfaecher`;
   * Spruenge aus Kontaktkarte, Kundenakte (SUP-11), Suche und Dashboard und
   * gespeicherte Lesezeichen kommen unveraendert an. Lesen mit
   * `support:sehen`, Status und Uebernahme mit `support:bearbeiten` (SUP-03).
   */
  {
    kennung: 'post',
    beschriftung: 'Postfächer',
    erklaerung: 'Die Mails an Info und Vertrieb — für Vertrieb und Service.',
    bereiche: [
      {
        kennung: 'posteingang',
        sinnbild: 'envelope-simple',
        beschriftung: 'Postfächer',
        erklaerung:
          'Die Mails an Info und Vertrieb als Vorgänge: lesen, Status setzen, Anhänge öffnen und '
          + 'Aussortiertes prüfen.',
        verlangt: { objekt: 'support', aktionen: ['sehen'] },
      },
    ],
  },
  {
    kennung: 'vertrieb',
    beschriftung: 'Vertrieb',
    erklaerung: 'Von der ersten Anfrage bis zum bestätigten Auftrag.',
    bereiche: [
      /*
       * DER VERTRIEB STEIGT BEI FIRMEN UND OEFFENTLICHEM DIENST EIN (Auftrag #9,
       * 27.09.2026). Vorbelegung und keine Sperre: Die beiden Kundenarten
       * stehen als abwaehlbare Marken ueber der Liste, und wer einen
       * Privatkunden sucht, waehlt sie ab. Das Recht ist dasselbe wie am
       * Gegenstueck im Service — es ist dieselbe Liste.
       */
      {
        kennung: 'kunden',
        sinnbild: 'address-book',
        beschriftung: 'Kunden',
        erklaerung:
          'Firmen und öffentlicher Dienst mit Ansprechpartnern, Anschriften, '
          + 'Rechnungsangaben und der Geschichte jedes Vorgangs.',
        verlangt: { objekt: 'kunde', aktionen: ['sehen'] },
      },
      /*
       * KUN-15: was der Person oder ihrer Rolle wieder vorgelegt wird. Neben
       * den Kunden und nicht unter Übersicht — die bleibt ein Punkt ohne
       * Pfeil (Auftrag CRM-49).
       */
      {
        kennung: 'wiedervorlagen',
        sinnbild: 'clock-counter-clockwise',
        beschriftung: 'Meine Wiedervorlagen',
        erklaerung:
          'Wiedervorlagen, die Ihnen oder Ihrer Rolle zugewiesen sind: fällige und überfällige '
          + 'abarbeiten, erledigte nachlesen.',
        verlangt: { objekt: 'kunde', aktionen: ['sehen'] },
      },
      /*
       * DAS RECHT IST `sehen` UND NICHT `anlegen`/`aendern` (SYS-01, Tabelle 11;
       * Auftrag #32). Buchhaltung und Geschaeftsleitung „sehen" Angebote; mit
       * dem Schreibrecht als Schluessel fehlte ihnen der Punkt ganz. Anlegen,
       * Aendern und Versenden bleiben in Liste und Akte an ihren Rechten.
       *
       * AUCH OPERATIONS SIEHT DEN PUNKT. Tabelle 11 nennt dort nur
       * „Terminoptionen beantworten" — aber die Arbeitsliste der Anfragen
       * fuehrt ueber die Angebotsnummer in die Angebotsakte (ANG-38: wer
       * antwortet, muss wissen, was verkauft wird; ohne Planungsrecht auch
       * ueber den Kunden), und die verlangt `angebot:sehen`. Ohne ein
       * neues, engeres Recht laesst sich Operations deshalb nicht herausnehmen.
       */
      {
        kennung: 'angebote',
        sinnbild: 'file-text',
        beschriftung: 'Angebote',
        erklaerung:
          'Angebote schreiben, versenden und nachfassen. Aus einem angenommenen '
          + 'Angebot wird der Auftrag.',
        verlangt: { objekt: 'angebot', aktionen: ['sehen'] },
      },
      {
        kennung: 'auftraege',
        sinnbild: 'clipboard-text',
        beschriftung: 'Aufträge',
        erklaerung:
          'Bestätigte Aufträge mit ihren Leistungen, Terminen und dem Stand der '
          + 'Fakturierung.',
        verlangt: { objekt: 'auftrag', aktionen: ['sehen'] },
      },
    ],
  },
  /*
   * ============================================================================
   * SERVICE: WAS AM TRESEN GEBRAUCHT WIRD
   * ============================================================================
   *
   * Auftraggeber am 11.09.2026: "neuer hauptmenuepunkt Service, darunter den
   * punkt buchungen (Service soll die Inhalte fuer den Empfang beinhalten)."
   *
   * Die Gruppe heisst wie die Anwendergruppe, die darin arbeitet (Tabelle 11,
   * i.d.F. AeA-01: `service` und `empfang`). Beide tragen `buchung/sehen` —
   * damit steht der Punkt bei genau denen, fuer die er gedacht ist.
   *
   * SIE WAR ABSICHTLICH KURZ. Der dritte Punkt ist am 13.09.2026 dazugekommen:
   * der Empfang am Kurstag (TAG-73). Das Recht `tagesplan/arbeitsliste_sehen`
   * stand seit 0006 und der Empfang trug es — die Liste gab es nicht.
   *
   * Die Teilnehmerliste am Termin bleibt, wo sie ist (BUC-53): Sie ist der
   * AUSDRUCK fuer das Tor. Der Empfang ist die Bildschirmfassung fuer den
   * Tresen, und beide zeigen dieselben Plaetze aus verschiedenen Richtungen.
   */
  {
    kennung: 'service',
    beschriftung: 'Service',
    erklaerung:
      'Was am Empfang gebraucht wird: Buchungen, was an ihnen noch fehlt, und die '
      + 'Privatkunden.',
    bereiche: [
      /*
       * DIE KUNDEN DES SERVICE (Tab. 11 i.d.F. AeA-01, Auftrag #9).
       *
       * „Service: alle Kunden aendern, vorbelegt auf Privatkunden" — ein
       * Filtervorschlag und kein eigener Sichtbarkeitsbereich. Es ist dieselbe
       * Liste wie im Vertrieb, mit anderer Vorbelegung; die Akte, die man von
       * hier oeffnet, laesst den Punkt im Service stehen.
       *
       * VORN wie im Vertrieb (Auftrag #10, Entscheidung vom 27.09.2026): Beide
       * Gruppen beginnen mit ihren Kunden. Buchungen, Arbeitsliste, Absagen,
       * Widerrufe und Empfang folgen in ihrer bisherigen Reihenfolge.
       */
      {
        kennung: 'privatkunden',
        sinnbild: 'address-book',
        beschriftung: 'Kunden',
        erklaerung:
          'Privatkunden suchen, anlegen und pflegen — mit Buchungen, Anschriften und '
          + 'der Geschichte jedes Vorgangs.',
        verlangt: { objekt: 'kunde', aktionen: ['sehen'] },
      },
      /*
       * Der Weg hinein ist die Buchungsnummer vom Beleg des Kunden — wer am
       * Telefon nach einer Stornogebuehr gefragt wird, sucht keinen Termin. Das
       * Recht ist `buchung:sehen`: Rechnen ist kein Eingriff, und wer die
       * Auskunft geben soll, muss sie ausrechnen duerfen, ohne stornieren zu
       * duerfen (BUC-45).
       */
      {
        kennung: 'buchungen',
        sinnbild: 'ticket',
        beschriftung: 'Buchungen',
        erklaerung:
          'Eine Buchung über ihre Nummer finden und bearbeiten: umbuchen, stornieren, '
          + 'Teilnehmer nachtragen, Gebühren ausrechnen.',
        verlangt: { objekt: 'buchung', aktionen: ['sehen'] },
      },
      /*
       * DIE ARBEITSLISTE IST DER EINSTIEG UND KEINE BEIGABE (B5, 12.09.2026).
       *
       * BUC-70: "Fehlende Teilnehmerdaten werden vor Fristablauf erinnert und
       * ALS ARBEITSLISTE GEFUEHRT." Sie gab es — als Block unter der gesuchten
       * Buchung, in einer Maske ueber eine EINZELNE Buchung. Wer morgens fragt,
       * was heute zu tun ist, hat aber keine Buchungsnummer im Kopf.
       *
       * Jetzt ist sie ihr eigener Punkt: nach Frist sortiert, mit dem Weg in die
       * Buchung. Das Recht ist dasselbe — `buchung/sehen` —, denn es ist
       * dieselbe Auskunft aus der anderen Richtung.
       */
      {
        kennung: 'arbeitsliste',
        sinnbild: 'list-checks',
        beschriftung: 'Arbeitsliste',
        erklaerung:
          'Buchungen, bei denen Teilnehmernamen fehlen — nach Meldefrist sortiert, das '
          + 'Dringendste oben.',
        verlangt: { objekt: 'buchung', aktionen: ['sehen'] },
      },
      /*
       * DIE ABSAGELISTE (BUC-129, 19.09.2026). Nach einer Terminabsage die
       * Arbeitsliste des Supports: je Buchung umbuchen oder erstatten. Sehen
       * darf sie, wer Termine sieht; bearbeiten nur mit `termin:umbuchen`.
       */
      {
        kennung: 'absagen',
        sinnbild: 'calendar-x',
        beschriftung: 'Absagen',
        erklaerung:
          'Buchungen abgesagter Termine: Kontakt, Umbuchung oder Erstattung — bis keine '
          + 'mehr offen ist.',
        verlangt: { objekt: 'termin', aktionen: ['sehen'] },
      },
      /*
       * DER WIDERRUF IM HAUS (LEG-17, Paket 8 G, 26.09.2026). Was ueber die
       * Widerrufsseite oder per Mail und Brief einging, wartet hier auf die
       * Entscheidung. Sehen mit `buchung:sehen`, entscheiden nur mit
       * `buchung:stornieren` -- die Anerkennung ist ein Storno.
       */
      {
        kennung: 'widerrufe',
        sinnbild: 'arrow-u-up-left',
        beschriftung: 'Widerrufe',
        erklaerung:
          'Eingegangene Widerrufe: Frist und Recht je Buchung, Eingangsbestätigung, '
          + 'anerkennen oder ablehnen.',
        verlangt: { objekt: 'buchung', aktionen: ['sehen'] },
      },
      /*
       * DER EMPFANG AM KURSTAG (TAG-73, 13.09.2026).
       *
       * Der Punkt, der oben als NOCH-OFFEN gefuehrt war: „Der Empfang sieht
       * diese Faelle in einer Arbeitsliste" — Teilnehmer, die beim Check-in
       * keiner Gruppe zugeordnet sind. Das Recht `tagesplan/arbeitsliste_sehen`
       * stand seit 0006 und der Empfang traegt es; die Liste gab es nicht.
       *
       * ER STEHT NEBEN DER ARBEITSLISTE UND NICHT DARIN. Beide zeigen fehlende
       * Teilnehmernamen, und trotzdem sind es zwei Fragen: Die Arbeitsliste
       * fragt „was ist bis zur Frist nachzureichen" und sortiert nach Frist;
       * der Empfang fragt „wer steht heute vor mir" und sortiert nach Termin.
       */
      {
        kennung: 'empfang',
        sinnbild: 'door-open',
        beschriftung: 'Empfang',
        erklaerung:
          'Die Plätze des Tages je Termin: Anwesenheit abhaken und die Plätze klären, '
          + 'zu denen kein Name gemeldet ist.',
        verlangt: { objekt: 'tagesplan', aktionen: ['arbeitsliste_sehen'] },
      },
      /*
       * DIE GOOGLE-REZENSIONEN (SUP-53, AeA-51). Lesen mit `rezension:sehen`,
       * antworten mit `rezension:beantworten` (SUP-54).
       */
      {
        kennung: 'rezensionen',
        sinnbild: 'chat-circle-text',
        beschriftung: 'Rezensionen',
        erklaerung:
          'Die Google-Rezensionen der Standorte: offene lesen und beantworten, den Sendezustand '
          + 'jeder Antwort sehen.',
        verlangt: { objekt: 'rezension', aktionen: ['sehen'] },
      },
    ],
  },
  /*
   * DIE FAKTURA STEHT FUER SICH, AUCH MIT EINEM EINTRAG.
   *
   * Sie ist die zweite der drei Saeulen. Sie unter den Vertrieb zu haengen
   * hiesse, die Belegkette mit der Zustaendigkeit zu verwechseln.
   *
   * DER PUNKT HAENGT AN `rechnung:sehen` UND NICHT MEHR AN `buchen` (SYS-01,
   * Tabelle 11; Auftrag #32): Vertrieb, Service und Geschaeftsleitung „sehen"
   * Rechnungen. Buchen, Stornieren und Versenden bleiben in der Akte
   * an ihren Rechten — wer nur sieht, bekommt dort keinen Knopf. Gemahnt
   * wird in Navision (RNG-25); das CRM zeigt den Mahnstand nur an.
   */
  {
    kennung: 'faktura',
    beschriftung: 'Faktura',
    erklaerung: 'Die Belegkette von der Rechnung bis zum offenen Posten.',
    bereiche: [
      {
        kennung: 'rechnungen',
        sinnbild: 'receipt',
        beschriftung: 'Rechnungen',
        erklaerung:
          'Rechnungen erzeugen, prüfen und festschreiben; offene Posten und den Mahnstand '
          + 'aus Navision im Blick behalten.',
        verlangt: { objekt: 'rechnung', aktionen: ['sehen'] },
      },
    ],
  },
  {
    kennung: 'betrieb',
    beschriftung: 'Betrieb',
    erklaerung: 'Der laufende Tag: welche Termine stattfinden, wie sie geplant sind und wer sie hält.',
    bereiche: [
      {
        kennung: 'termine',
        sinnbild: 'calendar-blank',
        beschriftung: 'Termine',
        erklaerung:
          'Alle Termine mit Status, Auslastung und Veröffentlichung — der Kalender des '
          + 'Hauses.',
        verlangt: { objekt: 'termin', aktionen: ['sehen'] },
      },
      /*
       * DIE TRAININGSPLANUNG IST EIN EIGENER BEREICH.
       *
       * „Ein Tag besteht aus verschiedenen Trainings und verschiedenen
       * Anfangszeiten" (Auftraggeber, 07.09.2026, AeA-08). Der Gegenstand ist
       * also der TAG und nicht das Programm — als Reiter am Produkt waere die
       * Planung nur ueber ein Training erreichbar, und wer den Tag plant, hat
       * keines im Kopf, sondern zwanzig.
       *
       * Das Recht ist `tagesplan/sehen` und nicht `planen`: Wer am Tag arbeitet,
       * muss ihn lesen koennen — vom Trainer bis zum Empfang. Was darin
       * verschoben werden darf, entscheidet die Maske am Recht `planen`.
       */
      {
        kennung: 'trainingsplanung',
        sinnbild: 'chart-bar-horizontal',
        beschriftung: 'Trainingsplanung',
        erklaerung:
          'Der einzelne Betriebstag auf der Zeitachse: Bausteine, Gruppen, Module und '
          + 'Pausen legen und verschieben.',
        verlangt: { objekt: 'tagesplan', aktionen: ['sehen'] },
      },
      /*
       * DIE ANFRAGEN STEHEN IM BETRIEB (Auftrag CRM-69, Ulf am 06.10.2026: „der
       * punkt anfragen soll mit in den betrieb"). Beantwortet werden sie von der
       * Disposition, und die Antwort haengt am belegten Tag — deshalb neben der
       * Trainingsplanung, in die jede Zeile fuehrt. Pfad und Recht bleiben.
       */
      {
        kennung: 'anfragen',
        sinnbild: 'calendar-dots',
        beschriftung: 'Anfragen',
        erklaerung:
          'Terminwünsche aus Angeboten, die eine Freigabe brauchen — mit Wunschterminen, '
          + 'Teilnehmerzahl und Restfrist.',
        /*
         * DAS RECHT IST `terminoption_freigeben` UND NICHT `freigeben` (ANG-38).
         *
         * `angebot/freigeben` ist die Vier-Augen-Freigabe eines Angebots nach
         * ANG-20; sie traegt heute keine Rolle. Wer Terminoptionen beantwortet,
         * traegt `terminoption_freigeben` — Operations, seit 0006. Mit dem
         * falschen Recht war der Menuepunkt fuer JEDEN unsichtbar, auch fuer
         * die, deren Arbeitsliste dahinter liegt.
         */
        verlangt: { objekt: 'angebot', aktionen: ['terminoption_freigeben'] },
      },
      /*
       * DIE BESETZUNG BLEIBT IM BETRIEB UND GEHT NICHT ZU DEN RESSOURCEN.
       *
       * "Die Besetzung mit externen Dienstleistern erfolgt NACHGELAGERT zur
       * Buchung" (RES-49). Sie ist damit eine laufende Aufgabe des Betriebs
       * und keine Stammdatenpflege — wer morgens nachsieht, was heute zu tun
       * ist, sucht keinen Trainerstamm, sondern eine Liste mit Fristen.
       *
       * Das Recht ist `ressource/sehen` und nicht `zuordnen`: Wer am Telefon
       * Auskunft geben soll, muss den Stand lesen duerfen, ohne anfragen zu
       * duerfen. Was angefragt werden darf, entscheidet die Maske am Recht
       * `zuordnen`.
       */
      {
        kennung: 'besetzung',
        sinnbild: 'user-switch',
        beschriftung: 'Besetzung',
        erklaerung:
          'Termine, denen noch ein Trainer oder ein Fahrzeug fehlt — mit Frist, '
          + 'Kandidatenliste und dem Weg zur Anfrage.',
        verlangt: { objekt: 'ressource', aktionen: ['sehen'] },
      },
    ],
  },
  /*
   * ============================================================================
   * RESSOURCEN: ZWEI PUNKTE, ZWEI ARBEITSGEGENSTAENDE
   * ============================================================================
   *
   * Auftraggeber am 11.09.2026: "ressourcenmenue mit zwei punkten Assets und
   * Trainer."
   *
   * RES-01 verlangt EIN Modell fuer Personen, Raeume, Geraete und Fahrzeuge, und
   * dabei bleibt es — im Menue sind es trotzdem zwei Punkte, weil es zwei
   * Arbeitsgegenstaende sind: Am Fahrmodul pflegt man Bausteine, Platzzahlen und
   * Wartung, am Trainer Qualifikationen, Rahmenvereinbarung und Honorar. Wer das
   * eine tut, tut das andere nicht in derselben Stunde.
   *
   * EINE EIGENE GRUPPE und nicht zwei Punkte im Betrieb: Der Betrieb ist der
   * laufende Tag — Termine, Planung, Besetzung. Der Ressourcenstamm ist das, was
   * dafuer bereitsteht. Zwei Stammpunkte mitten in der Tagesliste waeren die
   * Gliederung, die man beim Lesen wieder selbst machen muss.
   *
   * Das Recht ist an beiden `ressource/zuordnen`, unveraendert gegenueber dem
   * einen Punkt davor: Es ist derselbe Stamm.
   */
  {
    kennung: 'ressourcen',
    beschriftung: 'Ressourcen',
    erklaerung: 'Was für ein Training bereitstehen muss — und wer es hält.',
    bereiche: [
      /*
       * DAS RECHT IST `sehen` UND NICHT `zuordnen` (B2, 12.09.2026).
       *
       * Bis zum Review stand hier `zuordnen`. Das traegt nur Operations — und
       * damit erreichte die ADMINISTRATION den Stamm nicht, obwohl sie ihn
       * pflegen darf (`ressource/pflegen`) und obwohl Rahmenvereinbarung und
       * Honorarsaetze eines Trainers in seiner Akte liegen. Sie durfte beides
       * ueber die Schnittstelle und nicht ueber die Oberflaeche.
       *
       * Dieselbe Ueberlegung wie bei der Besetzung: Wer am Telefon sagen soll,
       * ob ein Raum frei ist, muss nachsehen duerfen, ohne zu disponieren. Was
       * geaendert werden darf, entscheidet die Maske am Recht — und der Server
       * in jedem Fall (BEN-08).
       */
      {
        kennung: 'assets',
        sinnbild: 'car',
        beschriftung: 'Assets',
        erklaerung:
          'Räume, Fahrmodule, Fahrzeuge und Geräte: Stammdaten, Platzzahlen, '
          + 'Belegungsart und Wartung.',
        verlangt: { objekt: 'ressource', aktionen: ['sehen'] },
      },
      {
        kennung: 'trainer',
        sinnbild: 'users-three',
        beschriftung: 'Trainer',
        erklaerung:
          'Trainer und Dienstleister: Qualifikationen mit ihren Nachweisen, '
          + 'Rahmenvereinbarung, Absagefristen und Honorar.',
        verlangt: { objekt: 'ressource', aktionen: ['sehen'] },
      },
    ],
  },
  {
    kennung: 'stammdaten',
    beschriftung: 'Stammdaten',
    erklaerung: 'Was die Arbeit voraussetzt: das Angebot des Hauses und seine Sätze.',
    bereiche: [
      {
        kennung: 'produkte',
        sinnbild: 'tag',
        beschriftung: 'Produkte',
        erklaerung:
          'Trainings und Artikel mit Preisen, Varianten, Staffeln und dem, was sie an '
          + 'Ressourcen brauchen.',
        verlangt: { objekt: 'artikel', aktionen: ['sehen'] },
      },
      /*
       * BAUSTEINE STEHEN NEBEN DEN PRODUKTEN, NICHT DARIN.
       *
       * Ein Baustein ist kein Artikel, sondern der Bauplan dahinter — dasselbe
       * Wort steht im Programm eines Produkts, in der Eignung eines Moduls und im
       * Tagesplan (PRD-29, PRD-30). Als Reiter in der Artikelakte waere er nur
       * ueber ein Produkt erreichbar, und wer die Module pflegt, sucht ihn dort
       * nicht.
       *
       * Das Recht ist `artikel/konfigurieren`: Wer Preise und Bezeichnungen
       * pflegen darf, darf damit noch nicht festlegen, woraus ein Training
       * besteht.
       */
      {
        kennung: 'bausteine',
        sinnbild: 'stack',
        beschriftung: 'Bausteine',
        erklaerung:
          'Der Bauplan hinter den Trainings: aus welchen Bausteinen ein Programm '
          + 'besteht, wie lange sie dauern und welche Module sie brauchen.',
        verlangt: { objekt: 'artikel', aktionen: ['sehen'] },
      },
      /*
       * DIE HONORARTABELLE STEHT BEI DEN STAMMDATEN UND IST TROTZDEM FUER FAST
       * NIEMANDEN SICHTBAR.
       *
       * RES-73: "Honorartabellen und Uebersteuerungen sind nur fuer berechtigte
       * Rollen sichtbar und aenderbar; DISPOSITION UND PLANUNG SEHEN SIE NICHT."
       *
       * Das Recht ist deshalb `artikel/honorar_sehen` und nicht
       * `artikel/sehen` — und es ist das einzige Recht in dieser Navigation,
       * das eine Sicht auf GELD oeffnet. Operations traegt es nicht, und ein
       * Trigger in 0017 laesst auch nicht zu, dass jemand es ihr gibt.
       *
       * Nicht bei den Ressourcen, obwohl die Honorare zu Kapitel 12 gehoeren:
       * Wer sie pflegt, pflegt keine Ressource, sondern einen Tarif. Wer eine
       * Ressource pflegt, darf sie ueberdies meist nicht sehen — ein Bereich,
       * den die halbe Gruppe nicht oeffnen kann, gehoert nicht in ihre Gruppe.
       */
      {
        kennung: 'honorare',
        sinnbild: 'coins',
        beschriftung: 'Honorare',
        erklaerung:
          'Was das Haus externen Trainern zahlt: je Fahrtraining ein Wochentags- und '
          + 'ein Wochenendtarif, dazu ein Stundensatz und ein Satz für die Koordination.',
        verlangt: { objekt: 'artikel', aktionen: ['honorar_sehen'] },
      },
    ],
  },
  /*
   * ============================================================================
   * BERICHTE: EINE EIGENE GRUPPE VOR SYSTEM (Auftrag #24, 29.09.2026)
   * ============================================================================
   *
   * Auftraggeber: „Menüpunkt soll ein eigener in der Menüleiste sein." Der
   * Vorschlag hatte die Berichte unter „Übersicht" neben dem Dashboard; die
   * Entscheidung ist eine eigene Gruppe. Bis hierher stand an dieser Stelle der
   * Platzhalter „Auswertungen" unter System — er entfällt, seine Adresse
   * leitet weiter (wegweiser.ts).
   *
   * EIN PUNKT, ALSO KEINE BEREICHSSEITE: Wie „Übersicht" führt die Gruppe
   * direkt zur Berichtsliste (#17). Mit `auswertung:sehen` sichtbar; welche
   * Kennzahlen darin stehen, entscheiden die Fachrechte (AUS-06).
   */
  {
    kennung: 'auswertung',
    beschriftung: 'Berichte',
    erklaerung: 'Eigene Auswertungen aus dem Kennzahlenkatalog.',
    bereiche: [
      {
        kennung: 'berichte',
        sinnbild: 'chart-bar',
        beschriftung: 'Berichte',
        erklaerung:
          'Kennzahlen wie Umsatz, Buchungen, Auslastung oder Trefferquote selbst '
          + 'zusammenstellen, gruppieren, filtern, speichern und als CSV ausgeben.',
        verlangt: { objekt: 'auswertung', aktionen: ['sehen'] },
      },
    ],
  },
  {
    kennung: 'system',
    beschriftung: 'System',
    erklaerung: 'Einstellungen, Umsysteme, Benutzer und der Zustand der Anlage.',
    bereiche: [
      /*
       * ============================================================================
       * AUS EINEM PUNKT "VERWALTUNG" WURDEN VIER (B4, 12.09.2026)
       * ============================================================================
       *
       * Hinter dem einen Punkt lagen ZWOELF Reiter, und sie mischten vier Welten:
       * Belegwesen, Umsysteme, Haus und Betrieb, Personen. Zwoelf Reiter in einer
       * Zeile sind keine Gliederung mehr, sondern ein zweites Menue.
       *
       * Der Reiter traegt drei bis fuenf Sichten AUF DENSELBEN BESTAND — so wie es
       * Auftraege, Produkte und Rechnungen tun. Danach ist er das falsche Mittel.
       *
       * Die Rechte sind dieselben wie vorher; erfunden wurde keines (BEN-02). Neu
       * ist, dass jeder REITER sein eigenes Recht tragen darf und ausgeblendet
       * wird, wenn es fehlt — bis hierher sah Operations den Reiter "Benutzer und
       * Rollen" und bekam beim Anfassen eine Abweisung.
       */
      {
        kennung: 'verwaltung',
        sinnbild: 'gear-six',
        beschriftung: 'Verwaltung',
        erklaerung:
          'Haus und Betrieb einstellen: Standorte, Öffnungszeiten, Nummernkreise, '
          + 'Bestätigungsläufe und die Datenübernahme.',
        verlangt: { objekt: 'einstellungen', aktionen: ['konfigurieren', 'sehen'] },
      },
      {
        kennung: 'belege',
        sinnbild: 'files',
        beschriftung: 'Belege und Vorlagen',
        erklaerung:
          'Was auf dem Papier steht und was eine Absage kostet: Belegvorlagen, '
          + 'Rechtstexte und Gebührenstaffeln.',
        /*
         * `rechtstext/sehen` ist das breiteste Recht dieser drei Sichten — es
         * traegt auch Vertrieb und Service. Beide muessen die geltenden
         * Stornosaetze und Rechtstexte lesen koennen: Die Saetze stammen aus den
         * eigenen Geschaeftsbedingungen (BUC-45), und wer am Telefon eine Gebuehr
         * nennt, nennt eine Klausel.
         */
        verlangt: { objekt: 'rechtstext', aktionen: ['sehen'] },
      },
      {
        kennung: 'umsysteme',
        sinnbild: 'plugs-connected',
        beschriftung: 'Umsysteme',
        erklaerung:
          'Die Zugänge nach außen: Postfächer, Navision, E-Rechnung, '
          + 'Zahlungsdienst und KI — samt dem, was in der Ausgangswarteschlange steht.',
        verlangt: { objekt: 'einstellungen', aktionen: ['sehen'] },
      },
      {
        kennung: 'benutzer',
        sinnbild: 'user-gear',
        beschriftung: 'Benutzer und Recht',
        /* Die Check-in-Tablets (TAG-49) seit Auftrag #56: Geräte, die sich anmelden. */
        erklaerung:
          'Benutzer, Rollen, Vertretungen und Check-in-Tablets: wer sich anmelden darf '
          + 'und was er dabei sehen und ändern kann.',
        verlangt: { objekt: 'einstellungen', aktionen: ['sehen'] },
      },
      /*
       * ============================================================================
       * DATENSCHUTZ IST EIN EIGENER PUNKT (B1, 12.09.2026)
       * ============================================================================
       *
       * Die Maske gab es. Erreichbar war sie als Reiter 8 von 12 unter
       * "Verwaltung" — und dieser Punkt verlangt `einstellungen/sehen`, das die
       * Rolle `datenschutz` nicht hat und nach AA-001 auch nicht haben SOLL: Wer
       * Protokolle kontrolliert, soll die Fachdaten nicht selbst sehen.
       *
       * Die Folge war, dass der Datenschutzbeauftragte sich anmeldete und eine
       * leere Startseite sah. Ein Auskunftsersuchen nach Artikel 15 DSGVO liess
       * sich nicht beantworten (LEG-10 bis LEG-13).
       *
       * Das Recht ist `zugriffsprotokoll/sehen` — es traegt genau zwei Rollen,
       * Datenschutz und Administration, und es existiert seit dem ersten
       * Rollenmodell. Derselbe Bau wie beim Systemzustand, den BET-22 aus
       * demselben Grund aus der Verwaltung herausgeholt hat.
       */
      {
        kennung: 'datenschutz',
        sinnbild: 'shield-check',
        beschriftung: 'Datenschutz',
        erklaerung:
          'Zugriffsprotokolle, Löschkonzept und die Rechte der Betroffenen: Auskunft '
          + 'nach Artikel 15, Einschränkung und Löschung.',
        verlangt: { objekt: 'zugriffsprotokoll', aktionen: ['sehen'] },
      },
      /*
       * EIN EIGENER BEREICH UND KEIN UNTERPUNKT DER VERWALTUNG.
       *
       * BET-22 verlangt die Statusseite ausdrücklich „auch für Empfang und
       * Disposition". Beide haben kein Verwaltungsrecht — unter `verwaltung`
       * bliebe sie genau denen verborgen, die sie am Tresen brauchen. Das eigene
       * Recht `umsystem:sehen` entkoppelt das.
       */
      /*
       * KAPITEL 21 STEHT FUER SICH UND NICHT UNTER DER VERWALTUNG.
       *
       * Die vier Nachweise (BP-11, BP-12, BP-40, BP-42, BP-43) ruft niemand
       * im Tagesgeschaeft auf — und wer sie braucht, braucht sie alle: die
       * Buchhaltung zum Monatsende, ein Pruefer an einem Tag im Jahr.
       * Verstreut in den Fachmasken waeren sie vor beiden versteckt.
       *
       * `auswertung:sehen` und kein eigenes Prueferrecht: BEN-02 zaehlt die
       * Rechteobjekte auf, und ein neues waere ein Aenderungsantrag. Das
       * Prueferkonto nach BP-01 wird ein Konto mit genau diesem Recht sein.
       */
      {
        kennung: 'betriebspruefung',
        sinnbild: 'scales',
        beschriftung: 'Betriebsprüfung',
        erklaerung:
          'Was noch festzuschreiben ist, welche Kontrollen laufen, wer wann was durfte — '
          + 'und die Geschichte der steuerrelevanten Konfiguration.',
        verlangt: { objekt: 'auswertung', aktionen: ['sehen'] },
      },
      {
        kennung: 'systemzustand',
        sinnbild: 'heartbeat',
        beschriftung: 'Systemzustand',
        erklaerung:
          'Ob die Umsysteme antworten und was sich in den Warteschlangen staut — die '
          + 'erste Seite, wenn etwas nicht geht.',
        verlangt: { objekt: 'umsystem', aktionen: ['sehen'] },
      },
    ],
  },
];

/**
 * Alle Bereiche flach, in der Reihenfolge der Spalte.
 *
 * Der Rechtefilter und die Pruefungen arbeiten darauf: Ob ein Punkt sichtbar
 * ist, entscheidet sein Recht und nicht seine Gruppe (4.3).
 */
export const BEREICHE: readonly Bereich[] = BEREICHSGRUPPEN.flatMap((g) => g.bereiche);

/**
 * DAS PRUEFERKONTO SIEHT NUR DIE PRUEFFLAECHE (BP-01, BP-02; Auftrag #32).
 *
 * Der Waechter der API laesst ein Prueferkonto nur an Endpunkte mit
 * `@FuerPruefbereich` — default-deny, weil BP-02 dem Kunden zusichert, dass ein
 * Pruefer keine Gespraechsvermerke und Einwilligungen sieht. Seine Rolle
 * traegt trotzdem `rechnung:sehen` (BP-03, Sprung auf den Einzelbeleg), und
 * seit „Rechnungen" an diesem Recht haengt, stuende der Punkt in seinem Menue
 * und endete mit 403. Das Menue zieht deshalb dieselbe Schranke wie der
 * Waechter: fuer diese Kontoart nur die Punkte der Pruefflaeche.
 */
const PRUEFFLAECHE: readonly Bereichskennung[] = ['betriebspruefung'];

function darfSehen(bereich: Bereich, rechte: readonly Recht[], kontoart: Benutzer['kontoart']): boolean {
  if (kontoart === 'pruefer' && !PRUEFFLAECHE.includes(bereich.kennung)) return false;
  return (
    bereich.verlangt === undefined
    || rechte.some(
      (r) => r.objekt === bereich.verlangt?.objekt && bereich.verlangt.aktionen.includes(r.aktion),
    )
  );
}

export function sichtbareBereiche(
  rechte: readonly Recht[],
  kontoart: Benutzer['kontoart'] = 'person',
): readonly Bereich[] {
  return BEREICHE.filter((b) => darfSehen(b, rechte, kontoart));
}

/**
 * Dieselbe Auswahl, nur gegliedert — und OHNE LEERE ÜBERSCHRIFTEN.
 *
 * Eine Gruppe, unter der für diesen Benutzer nichts steht, fällt ganz weg.
 * Stehen zu lassen hieße, ihm zu sagen „hier gibt es etwas, das du nicht
 * siehst"; das Menü ist eine Erleichterung und kein Verzeichnis dessen, was
 * andere dürfen (4.3, BEN-08).
 *
 * Die Reihenfolge der Bereiche innerhalb einer Gruppe bleibt die der
 * Definition — `sichtbareBereiche` liefert dieselbe Liste flach, und beide
 * müssen dasselbe sagen.
 */
export function sichtbareGruppen(
  rechte: readonly Recht[],
  kontoart: Benutzer['kontoart'] = 'person',
): readonly Bereichsgruppe[] {
  return BEREICHSGRUPPEN.map((g) => ({
    ...g,
    bereiche: g.bereiche.filter((b) => darfSehen(b, rechte, kontoart)),
  })).filter((g) => g.bereiche.length > 0);
}

/**
 * Zu welcher Gruppe ein Bereich gehoert.
 *
 * `null` fuer eine Kennung, die es nicht gibt — die Startseite schickt ihre
 * Sprungziele als Zeichenkette ueber die Schnittstelle, und eine Antwort aus
 * einer aelteren Fassung des Servers darf die Oberflaeche nicht zerbrechen
 * (ERR-08). Dann ist eben keine Gruppe offen.
 */
export function gruppeVonBereich(kennung: string): Bereichsgruppenkennung | null {
  return (
    BEREICHSGRUPPEN.find((g) => g.bereiche.some((b) => b.kennung === kennung))?.kennung ?? null
  );
}

/**
 * Das Seitenraster nach Abschnitt 2.3 des Pflichtenhefts in der Fassung von
 * ÄA-41 (Entwurf v2, 28.09.2026).
 *
 * Vier Zonen, immer an derselben Stelle: die KOPFZEILE mit Marke, globaler
 * Suche, „Neu", Hilfe, Glocke und Benutzer (das Zahnrad entfiel mit Auftrag
 * CRM-170); darunter
 * die MENÜZEILE mit den zehn Gruppen (seit Auftrag #24 mit „Berichte", seit CRM-185 mit „Postfächer"); der SEITENKOPF der Maske auf dem Grund;
 * darunter der Rumpf. Kopf- und Menüzeile kleben oben.
 *
 * Die Überschrift trägt `tabIndex={-1}` und bekommt bei jedem Maskenwechsel
 * den Fokus. Ohne das sagt ein Vorleseprogramm beim Wechsel nichts, und der
 * Benutzer steht am Anfang eines anderen Dokuments, ohne es zu merken
 * (NFA-07, WCAG 2.4.3).
 */
/**
 * Ob der Fokus gerade in einer Eingabe steht.
 *
 * Fuer druckbare Tastenwege ist das die entscheidende Frage: Ein "/" gehoert
 * in ein Suchfeld hinein, nicht daneben. `isContentEditable` deckt die
 * Textbausteine der Belegvorlagen mit ab.
 */
function imEingabefeld(ziel: EventTarget | null): boolean {
  if (!(ziel instanceof HTMLElement)) return false;
  if (ziel.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(ziel.tagName);
}

/** Die Anfangsbuchstaben für die Kürzelkachel — höchstens zwei. */
export function kuerzel(name: string): string {
  const teile = name.trim().split(/\s+/).filter((t) => t !== '');
  const erster = teile[0]?.[0] ?? '?';
  const letzter = teile.length > 1 ? (teile[teile.length - 1]?.[0] ?? '') : '';
  return (erster + letzter).toUpperCase();
}

/**
 * Der Tastenweg zur Suche, wie ihn die Tastatur beschriftet: ⌘K auf dem Mac
 * (so zeigt es der Entwurf), sonst Strg K. Beide wirken überall.
 */
function suchtaste(): string {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    ? '⌘K'
    : 'Strg K';
}

/**
 * Die Glocke (Auftrag CRM-170, ÄA-70): was dem Benutzer selbst gehört und
 * fällig ist. Die Einträge baut die Arbeitsfläche, sie kennt die Masken.
 */
export type Rahmenglocke = {
  /** Fällige eigene Einträge — die Zahl an der Glocke. */
  readonly faellig: number;
  /** Rot, sobald etwas überfällig ist. */
  readonly ueberfaellig: boolean;
  readonly eintraege: readonly Aufklappeintrag[];
};

/** Eine Anlagemaske für „Neu". */
export type Anlage = { readonly text: string; readonly beiWahl: () => void };

export function Rahmen({
  benutzer,
  rechte,
  bereich,
  gruppe,
  beiBereich,
  beiGruppe,
  titel,
  kicker,
  unterzeile,
  kennzeichen,
  sinnbild,
  kennzahlen,
  titelkarte,
  kompakt,
  kopfhandlungen,
  reiter,
  handlung,
  anlagen = [],
  glocke,
  beiSuchen,
  hilfe,
  beiAbmelden,
  beiMeinKonto,
  breit,
  children,
}: {
  readonly benutzer: Benutzer;
  readonly rechte: readonly Recht[];
  /**
   * Der Punkt, auf dem man steht. `null`, wenn gerade die Bereichsseite einer
   * Gruppe offen ist — dann ist kein einzelner Punkt der aktuelle.
   */
  readonly bereich: string | null;
  /** Die Gruppe der aktuellen Maske — sie steht in der Menüzeile hervorgehoben. */
  readonly gruppe: string | null;
  readonly beiBereich: (kennung: string) => void;
  readonly beiGruppe: (kennung: string) => void;
  readonly titel: string;
  /** Seit ÄA-41 ohne Wirkung: Jeder Rumpf nimmt die volle Breite. */
  readonly breit?: boolean | undefined;
  /** Die Auszeichnung über dem Titel — Art, Nummer, Kundenart. */
  readonly kicker?: string | undefined;
  /** Die ruhige Zeile unter dem Titel: Betreuer, Anschrift, Zeitraum. */
  readonly unterzeile?: string | undefined;
  /** Zustandsmarken im Seitenkopf — Status, Sperre, fällige Posten. */
  readonly kennzeichen?: ReactNode;
  /** Die Sinnbildkachel einer Akte (52 px, Entwurf). */
  readonly sinnbild?: Kachelsinnbild | undefined;
  /** Die Kennzahlenleiste einer Akte. */
  readonly kennzahlen?: ReactNode;
  /** Die Karte am Titel (Auftrag CRM-56). Ohne Angabe steht die Überschrift allein. */
  readonly titelkarte?: Titelkartenangabe | undefined;
  /** Der niedrige Kopf (Auftrag CRM-95). */
  readonly kompakt?: boolean | undefined;
  /** Handlungen, die die Maske selbst meldet — vor der Haupthandlung. */
  readonly kopfhandlungen?: ReactNode;
  /** Sichten auf denselben Bestand — unter dem Kopf. */
  readonly reiter?: ReactNode;
  /** Die häufigste Folgehandlung, ganz rechts im Seitenkopf. */
  readonly handlung?: ReactNode;
  /** Was „Neu" anbietet — nur die Anlagemasken, die der Benutzer darf. */
  readonly anlagen?: readonly Anlage[];
  /** Die eigenen fälligen Einträge (Auftrag CRM-170). `undefined`: Die Glocke steht nicht da. */
  readonly glocke?: Rahmenglocke | undefined;
  /**
   * M-07: Das Suchfeld der Kopfzeile OEFFNET ein Fenster; es sucht nicht
   * selbst. "Es oeffnet ohne Eingabe. Fokus genuegt." Deshalb steht hier eine
   * Schaltflaeche und kein Eingabefeld — ein Feld, in das man tippt und in
   * dem nichts erscheint, waere die schlechtere Luege.
   */
  readonly beiSuchen?: () => void;
  /**
   * Die Maskenhilfe (NFA-17, ÄA-46): das Hilfe-Symbol mit seinem Aufklapper,
   * an der Stelle zwischen „Neu" und der Glocke. Ohne Angabe fehlt das
   * Symbol — ein Symbol ohne Hilfe dahinter wäre ein Klick ohne Auskunft.
   */
  readonly hilfe?: ReactNode;
  readonly beiAbmelden: () => void;
  /** BEN-07, SYS-05: eigene Sitzungen und Geräte. */
  readonly beiMeinKonto?: () => void;
  readonly children: ReactNode;
}): JSX.Element {
  const ueberschrift = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    ueberschrift.current?.focus();
  }, [titel]);

  /*
   * ============================================================================
   * WIE SICH DIESES HAUS NENNT (0254)
   * ============================================================================
   *
   * Oben links stand bis zum 20.09.2026 „Fahrsicherheitszentrum / Hannover
   * CRM" im Quelltext, im Browserreiter „CRM & Tagesplanung". Mit mehreren
   * Mandanten liest der Anwender in Musterhausen damit den Namen des ersten
   * Hauses.
   *
   * DER FENSTERTITEL IST DER WICHTIGERE TEIL. Wer Produktion, Staging und
   * einen zweiten Kunden nebeneinander offen hat, unterscheidet sie NUR am
   * Reiter — gleiche Titel sind die Voraussetzung dafuer, im falschen Fenster
   * zu buchen.
   *
   * Der Rueckfall ist der bisherige Text: Antwortet die Einstellung nicht,
   * sieht die Maske aus wie vorher statt leer.
   */
  const [haus, setzeHaus] = useState('Fahrsicherheitszentrum');
  const [anwendung, setzeAnwendung] = useState('CRM');
  /*
   * ODER STATT DESSEN DAS ZEICHEN (0255), im Entwurf 30 px hoch. Der Rueckfall
   * ist `false`: Bis die Antwort da ist, steht der Text — erst ein Bild, dann
   * Text waere ein Sprung im Kopf bei jedem Seitenaufruf.
   */
  const [zeichen, setzeZeichen] = useState(false);

  useEffect(() => {
    let abgebrochen = false;
    ladeOberflaeche()
      .then((o) => {
        if (abgebrochen) return;
        if (o.haus !== undefined && o.haus !== '') setzeHaus(o.haus);
        if (o.anwendung !== undefined && o.anwendung !== '') setzeAnwendung(o.anwendung);
        if (o.fenstertitel !== undefined && o.fenstertitel !== '') document.title = o.fenstertitel;
        /* Nur `logo` schaltet um. Jeder andere Wert — auch einer, den diese
           Fassung noch nicht kennt — laesst den Text stehen. */
        setzeZeichen(o.marke === 'logo');
      })
      .catch(() => {
        /* Ohne Antwort bleibt der Rueckfall stehen — der Kopf ist kein Grund,
           eine Maske scheitern zu lassen. */
      });
    return () => {
      abgebrochen = true;
    };
  }, []);

  /**
   * Die Tastenwege aus Tabelle 3, die UEBERALL gelten (verbindlich,
   * "im Abnahmefall geprueft").
   *
   *   Strg+K / ⌘K   Globale Suche, mit Fokus im Feld
   *   /             Filterfeld der aktuellen Liste
   *
   * DER SCHRAEGSTRICH IST DER HEIKLE VON BEIDEN. Er ist ein DRUCKBARES
   * Zeichen: Wer in einem Suchfeld "8/2026" tippt, darf nicht stattdessen in
   * ein anderes Feld springen. Deshalb greift er nur, wenn der Fokus NICHT in
   * einer Eingabe steht — und nicht bei gedrueckter Steuerungs- oder
   * Alt-Taste, wo er auf manchen Belegungen ganz andere Zeichen erzeugt.
   */
  useEffect(() => {
    const beiTaste = (ereignis: KeyboardEvent): void => {
      if ((ereignis.ctrlKey || ereignis.metaKey) && ereignis.key.toLowerCase() === 'k') {
        ereignis.preventDefault();
        beiSuchen?.();
        return;
      }

      if (ereignis.key !== '/' || ereignis.ctrlKey || ereignis.metaKey || ereignis.altKey) return;
      if (imEingabefeld(ereignis.target)) return;

      /* "der aktuellen Liste" — es ist immer hoechstens eine offen. */
      const feld = document.querySelector<HTMLInputElement>('.rahmen__inhalt .liste__suche');
      if (feld === null) return;
      ereignis.preventDefault();
      feld.focus();
      feld.select();
    };
    window.addEventListener('keydown', beiTaste);
    return () => window.removeEventListener('keydown', beiTaste);
  }, [beiSuchen]);

  const gruppen = sichtbareGruppen(rechte, benutzer.kontoart);
  /* Die Menüzeile öffnet beim Überfahren, höchstens eine Gruppe zugleich (Auftrag #21). */
  const [schwebezeile] = useState<Schwebezeile>(() => ({ offen: null }));

  /*
   * Die Rolle beim Benutzer. Sie kommt aus den WIRKSAMEN RECHTEN und nicht aus
   * einem Feld am Konto: Ein Benutzer kann mehrere Rollen tragen (BEN-03), und
   * die Vertretung ist keine — sie steht als `vertretung:<name>` in derselben
   * Liste und wird hier ausgelassen, ebenso ein Zusatzrecht. Gezeigt wird die
   * Bezeichnung, nicht die Kennung (Auftrag CRM-79).
   */
  const rolle = rechte
    .flatMap(herkunftVon)
    .filter((h) => !h.kennung.startsWith('vertretung:') && h.kennung !== 'zusatzrecht')
    .map((h) => h.name)
    .sort((a, b) => a.localeCompare(b, 'de'))[0];
  const vertretungen = [
    ...new Set(
      rechte
        .flatMap((r) => r.ausRollen)
        .filter((r) => r.startsWith('vertretung:'))
        .map((r) => r.slice('vertretung:'.length)),
    ),
  ];

  /*
   * DIE HÖHE DES KOPFES ALS MASS (Auftrag CRM-105). Klebende Seitenleisten
   * und Vorschau rechnen ihren Abstand von oben damit. Bisher stand dort die
   * Summe der beiden festen Zeilenhöhen; bricht der Kopf auf einem schmalen
   * Schirm um, rutschten sie darunter. Gemessen werden nur Kopf- und
   * Menüzeile; der Kopf klebt unter dem Rand oben (CRM-193), und was unter
   * ihm liegt, rechnet den Rand selbst dazu.
   */
  const kopfRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const kopf = kopfRef.current;
    if (kopf === null || typeof ResizeObserver === 'undefined') return;
    const wurzel = document.documentElement;
    const beobachter = new ResizeObserver(() => {
      wurzel.style.setProperty('--kopfhoehe', `${String(kopf.offsetHeight)}px`);
    });
    beobachter.observe(kopf);
    return () => {
      beobachter.disconnect();
      wurzel.style.removeProperty('--kopfhoehe');
    };
  }, []);

  /*
   * DER TELEFONKOPF (Auftrag CRM-106, ÄA-63). Unter 600 px eine Zeile von
   * 56 px: Menü-Knopf mit Schublade statt Menüzeile; Suche und „Neu" nur als
   * Sinnbild regelt stil.css. Die Einstellungen standen bis Auftrag CRM-170 im
   * Benutzermenü; seitdem erreicht man sie wie am Rechner über das Menü
   * (System → Verwaltung → Einstellungen), das die Schublade vollständig trägt.
   */
  const telefon = useTelefon();
  const [schublade, setzeSchublade] = useState(false);
  const mehr = useRef<HTMLDivElement>(null);
  const [mehrOffen, setzeMehrOffen] = useState(false);
  useEffect(() => {
    if (!mehrOffen) return;
    const daneben = (e: PointerEvent): void => {
      if (!(e.target instanceof Node) || mehr.current?.contains(e.target) !== true) setzeMehrOffen(false);
    };
    const beiEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setzeMehrOffen(false);
    };
    document.addEventListener('pointerdown', daneben);
    document.addEventListener('keydown', beiEsc);
    return () => {
      document.removeEventListener('pointerdown', daneben);
      document.removeEventListener('keydown', beiEsc);
    };
  }, [mehrOffen]);

  return (
    <div className="rahmen">
      <header className="rahmen__kopf" ref={kopfRef}>
        {/*
          DIE KOPFZEILE DES ENTWURFS: 56 px, weiß. Links die Marke, mittig die
          Suche (höchstens 560 px), rechts die Handlungen des Rahmens.
        */}
        <div className="kopfzeile">
          {telefon && (
            <button
              type="button"
              className="kopfzeile__symbol kopfzeile__menue"
              aria-label="Menü"
              aria-haspopup="dialog"
              aria-expanded={schublade}
              onClick={() => setzeSchublade(true)}
            >
              <Sinnbild name="list" gewicht="light" />
            </button>
          )}
          <div className="kopfzeile__marke">
            {zeichen ? (
              /*
                DAS ZEICHEN TRÄGT DEN NAMEN ALS ALTERNATIVE (0255, NFA-17): Wer
                die Maske vorgelesen bekommt, hört dasselbe wie jeder andere —
                und weiß, in welcher Instanz er arbeitet. Feste Maße im Markup,
                damit der Kopf nicht springt, sobald das SVG geladen ist.
              */
              telefon ? (
                /*
                  Auf dem Telefon das Signet statt der Wortmarke (Auftrag CRM-106):
                  das Hütchen in der Kreisbahn, in den Farben des Desktop-Logos
                  und ohne Kachel — nicht das Favicon (Auftrag CRM-110).
                */
                <img
                  className="kopfzeile__zeichen"
                  src="/signet-tritixcrm.svg"
                  alt={`${haus} ${anwendung}`}
                  width={30}
                  height={30}
                />
              ) : (
                <img
                  className="kopfzeile__zeichen"
                  src="/marke-tritixcrm.svg"
                  alt={`${haus} ${anwendung}`}
                  width={662}
                  height={169}
                />
              )
            ) : (
              <>
                <p className="kopfzeile__haus">{haus}</p>
                <p className="kopfzeile__anwendung">{anwendung}</p>
              </>
            )}
          </div>

          <div className="kopfzeile__mitte">
            <button
              type="button"
              className="kopfzeile__suche"
              aria-label="Kunde, Beleg oder Termin suchen"
              aria-keyshortcuts="Control+K Meta+K"
              aria-haspopup="dialog"
              onClick={() => beiSuchen?.()}
              /* "Fokus genuegt" (M-07). */
              onFocus={() => beiSuchen?.()}
            >
              <Sinnbild name="magnifying-glass" gewicht="light" />
              <span className="kopfzeile__suchtext">Kunden, Kontakte, Belege, Termine durchsuchen</span>
              <span className="taste" aria-hidden="true">
                {suchtaste()}
              </span>
            </button>
          </div>

          <div className="kopfzeile__rechts">
            {/* „Neu" bietet nur an, was der Benutzer anlegen darf (Auftrag #17). */}
            {anlagen.length > 0 && (
              <Aufklapper
                beschriftung="Neu anlegen"
                knopfklasse="kopfzeile__neu"
                knopf={
                  <>
                    <Sinnbild name="plus" gewicht="light" />
                    <span className="kopfzeile__neutext">Neu</span>
                  </>
                }
                eintraege={anlagen.map((a) => ({ text: a.text, beiWahl: a.beiWahl }))}
                rechtsbuendig
              />
            )}
            {/* Die Hilfe der aktuellen Maske, aufgeklappt unter ihrem Symbol (ÄA-46). */}
            {hilfe}
            {/*
              DIE GLOCKE ZEIGT NUR, WAS DEM BENUTZER SELBST GEHÖRT (Auftrag
              CRM-170, ÄA-70). Nutzer am 10.10.2026: „die Aufgabenanzeige soll
              nur eigene aufgaben und wiedervorlagen anzeigen." Bis dahin trug
              sie die hausweiten Aufgaben des Dashboards (ÄA-41); die stehen
              weiter in „Das steht an". Die Zahl sind die fälligen Einträge,
              rot ist sie, sobald einer überfällig ist.

              Das Zahnrad daneben entfiel mit demselben Auftrag: Die
              Einstellungen liegen im Menü unter System → Verwaltung.
            */}
            {glocke !== undefined && (
              <Aufklapper
                beschriftung={
                  glocke.faellig === 0 ? 'Meine fälligen Aufgaben: keine' : `Meine fälligen Aufgaben: ${glocke.faellig}`
                }
                knopfklasse="kopfzeile__symbol kopfzeile__glocke"
                knopf={
                  <>
                    <Sinnbild name="bell" gewicht="light" />
                    {glocke.faellig > 0 && (
                      <span
                        className={`kopfzeile__zahl${glocke.ueberfaellig ? ' kopfzeile__zahl--dringend' : ''}`}
                        aria-hidden="true"
                      >
                        {glocke.faellig > 99 ? '99+' : glocke.faellig}
                      </span>
                    )}
                  </>
                }
                kopf={
                  /* „Alle meine Wiedervorlagen" steht auch ohne Fälliges da — dann sagt der Kopf, dass nichts ansteht. */
                  glocke.faellig === 0 && glocke.eintraege.length > 0 ? (
                    <>
                      Meine fälligen Aufgaben
                      <span className="aufklapper__unterzeile">Nichts fällig.</span>
                    </>
                  ) : (
                    'Meine fälligen Aufgaben'
                  )
                }
                schliesskreuz
                leer="Nichts fällig."
                eintraege={glocke.eintraege}
                rechtsbuendig
              />
            )}
            <Aufklapper
              beschriftung={`Benutzer ${benutzer.name}`}
              klasse="kopfzeile__benutzer"
              knopfklasse="kopfzeile__person"
              knopf={
                <>
                  <span className="kopfzeile__kuerzel" aria-hidden="true">
                    {kuerzel(benutzer.name)}
                  </span>
                  <span className="kopfzeile__wer">
                    <span className="kopfzeile__name">{benutzer.name}</span>
                    <span className="kopfzeile__rolle">
                      {rolle ?? benutzer.anmeldename}
                      {vertretungen.length > 0 && ` · vertritt ${vertretungen.join(', ')}`}
                    </span>
                  </span>
                  <Sinnbild name="caret-down" gewicht="light" klasse="kopfzeile__pfeil" />
                </>
              }
              eintraege={[
                /*
                  BEN-07, SYS-05: „Mein Konto" gehört zur Person, nicht zur
                  Arbeit. Ein Recht braucht es nicht.
                */
                ...(beiMeinKonto === undefined
                  ? []
                  : [
                      {
                        text: 'Mein Konto',
                        vorne: <Sinnbild name="user-circle" gewicht="light" />,
                        beiWahl: beiMeinKonto,
                      },
                    ]),
                {
                  text: 'Abmelden',
                  vorne: <Sinnbild name="sign-out" gewicht="light" />,
                  beiWahl: beiAbmelden,
                },
              ]}
              rechtsbuendig
            />
          </div>
        </div>

        {/*
          ============================================================
          DIE MENÜZEILE: ACHT GRUPPEN, JEDE MIT IHREN PUNKTEN (ÄA-41)
          ============================================================

          Die Gruppen in der Folge aus #9. Eine Gruppe mit mehreren Punkten
          ist seit Auftrag #25 ZWEIERLEI: ihr Name ist ein Verweis auf ihre
          Übersichtsseite (die Bereichsseite), der Pfeil daneben ein Knopf,
          der ihre Punkte als Aufklapper öffnet. Jeder Punkt trägt das
          Sinnbild seiner Karte auf der Übersichtsseite, hier in Light wie
          alles im Rahmen. Eine Gruppe mit einem einzigen SICHTBAREN Punkt
          führt direkt dorthin — ein Aufklapper mit einem Eintrag wäre ein
          Klick, der nichts erklärt.

          EINE RUHIGE REITERZEILE (ÄA-68, Auftraggeber 08.10.2026: „sieht
          durch die unterschiedlichen Abstände sehr unruhig aus"). Jede Gruppe
          ist ein gleich gebauter Reiter, ob einfacher Knopf oder geteilt; der
          Pfeil nimmt keinen Platz und erscheint nur bei Tastaturfokus
          (stil.css). Ein Finger öffnet mit dem Tippen auf den Namen die
          Liste (Aufklapper.tsx), und deren erster Eintrag ist wieder
          „Übersicht" — abgesetzt durch eine Linie, aktuell auf der
          Bereichsseite. Am Rechner mit Maus oder Trackpad fehlt er seit
          Auftrag CRM-148 (Nutzer, 09.10.2026: „da man hierfür direkt auf den
          Eintrag in der leiste klicken kann"); dort ist der Name der Weg.

          Die Gruppe wählt nicht das Menü aus (4.3): Jeder Punkt trägt sein
          Recht, und eine Gruppe ohne sichtbaren Punkt steht gar nicht da.

          Mit der Maus öffnet eine Gruppe beim Überfahren (Auftrag #21, ÄA-41;
          `schwebe.ts`); die Tastatur geht wie zuvor (NFA-07). Nur
          hier: „Neu", Hilfe, Glocke und Benutzermenü öffnen beim Klick. Ein Punkt
          ohne Pfeil öffnet nichts, schließt aber eine überfahrene Gruppe
          sofort, sobald die Maus auf ihm steht (Auftrag CRM-49).
        */}
        {telefon ? (
          schublade && (
            <Schublade
              gruppen={gruppen}
              gruppe={gruppe}
              bereich={bereich}
              beiGruppe={beiGruppe}
              beiBereich={beiBereich}
              beiSchliessen={() => setzeSchublade(false)}
            />
          )
        ) : (
        <nav className="menuezeile" aria-label="Bereiche">
          <ul className="menuezeile__gruppen">
            {gruppen.map((g) => {
              const hier = g.kennung === gruppe;
              const einziger = g.bereiche.length === 1 ? g.bereiche[0] : undefined;
              const klasse = `menuezeile__gruppe${hier ? ' menuezeile__gruppe--hier' : ''}`;
              return (
                <li key={g.kennung}>
                  {einziger !== undefined ? (
                    <button
                      type="button"
                      className={klasse}
                      id={`bereichsgruppe-${g.kennung}`}
                      aria-current={hier ? 'page' : undefined}
                      onClick={() => beiGruppe(g.kennung)}
                      onPointerEnter={(e) => eintrittOhnePfeil(schwebezeile, e)}
                    >
                      {g.beschriftung}
                    </button>
                  ) : (
                    <Aufklapper
                      beschriftung={g.beschriftung}
                      kennung={`bereichsgruppe-${g.kennung}`}
                      klasse="menuezeile__geteilt"
                      knopfklasse={klasse}
                      /*
                        AUFTRAG #25, NFA-07, NFA-14: der Name ein Verweis auf die
                        Übersichtsseite, der Pfeil ein Knopf für die Liste — zwei
                        Elemente mit je einer Bedeutung (WCAG 4.1.2). Auf der
                        Übersichtsseite ist der Verweis die Seite (`page`), unter
                        einem ihrer Punkte „hier darin" (`true`).
                      */
                      verweis={{
                        href: `/${g.kennung}`,
                        inhalt: g.beschriftung,
                        beiWahl: () => beiGruppe(g.kennung),
                      }}
                      knopf={<Sinnbild name="caret-down" gewicht="light" klasse="menuezeile__pfeil" />}
                      aktuell={hier ? (bereich === null ? 'page' : 'true') : undefined}
                      zeile={schwebezeile}
                      /*
                        ÄA-68: oben „Übersicht" mit dem Sinnbild der Schublade,
                        durch eine Linie abgesetzt — der Weg zur Bereichsseite
                        für den Finger, der auf dem Namen die Liste öffnet.
                        Seit Auftrag CRM-148 (Nutzer, 09.10.2026) nur dort: Mit
                        der Maus führt der Name selbst hin, und die Liste
                        beginnt ohne Linie mit den Bereichen.
                      */
                      eintraege={[
                        {
                          text: 'Übersicht',
                          vorne: <Sinnbild name="squares-four" gewicht="light" />,
                          aktuell: hier && bereich === null,
                          abgesetzt: true,
                          nurFuerFinger: true,
                          beiWahl: () => beiGruppe(g.kennung),
                        },
                        ...g.bereiche.map((b) => ({
                          text: b.beschriftung,
                          vorne: <Sinnbild name={b.sinnbild} gewicht="light" />,
                          aktuell: hier && b.kennung === bereich,
                          beiWahl: () => beiBereich(b.kennung),
                        })),
                      ]}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
        )}
      </header>

      <main className="rahmen__inhalt">
        {/*
          DER SEITENKOPF AUF DEM GRUND (Entwurf v2).

          Bei einer Akte mit Sinnbildkachel (52 px), Auszeichnung in Versalien,
          Titel 28 px und darunter der Kennzahlenleiste; bei den übrigen Masken
          Titel und Unterzeile. Die Handlungen stehen rechts, die Haupthandlung
          blau ganz rechts.

          Kompakt (Auftrag CRM-95) etwa halb so hoch: Kachel 36 px, Titel
          20 px, die Kennzahlen in einer Zeile, soweit sie passen.
        */}
        <div className={kompakt === true ? 'seitenkopf seitenkopf--kompakt' : 'seitenkopf'}>
          <div className="seitenkopf__reihe">
            {sinnbild !== undefined && (
              <span className="seitenkopf__kachel" aria-hidden="true">
                <Kachelzeichen sinnbild={sinnbild} />
              </span>
            )}
            <div className="seitenkopf__identitaet">
              {kicker !== undefined && <p className="k seitenkopf__kicker">{kicker}</p>}
              <div className="seitenkopf__titelzeile">
                {/*
                  Mit Titelkarte hängt die Überschrift in deren Hülle (CRM-56).
                  Der Schlüssel ist der Titel: Eine andere Akte beginnt mit
                  geschlossener Karte.
                */}
                {titelkarte === undefined ? (
                  <h1 className="seitenkopf__titel" tabIndex={-1} ref={ueberschrift}>
                    {titel}
                  </h1>
                ) : (
                  <Titelkarte key={titel} karte={titelkarte}>
                    <h1 className="seitenkopf__titel" tabIndex={-1} ref={ueberschrift}>
                      {titel}
                    </h1>
                  </Titelkarte>
                )}
                {/* Zustand und Zähler stehen am Namen, nicht irgendwo in der Reihe. */}
                {kennzeichen !== undefined && <div className="seitenkopf__marken">{kennzeichen}</div>}
              </div>
              {unterzeile !== undefined && <p className="seitenkopf__unterzeile">{unterzeile}</p>}
            </div>
            {(kopfhandlungen !== undefined || handlung !== undefined) && (
              <div className="seitenkopf__handlung">
                {telefon && kopfhandlungen !== undefined ? (
                  /*
                    Auf dem Telefon bleibt nur die Haupthandlung als Knopf; die
                    Nebenhandlungen klappen unter „⋯" auf (Auftrag CRM-106).
                    Steht die Haupthandlung selbst unter den Kopfhandlungen,
                    lässt stil.css sie geschlossen sichtbar. Eine Wahl im
                    offenen Menü schließt es.
                  */
                  <div className={mehrOffen ? 'seitenkopf__mehr seitenkopf__mehr--offen' : 'seitenkopf__mehr'} ref={mehr}>
                    <button
                      type="button"
                      className="knopf seitenkopf__mehrknopf"
                      aria-label="Weitere Handlungen"
                      aria-expanded={mehrOffen}
                      onClick={() => setzeMehrOffen(!mehrOffen)}
                    >
                      <Sinnbild name="dots-three" gewicht="light" />
                    </button>
                    <div className="seitenkopf__mehrliste" onClick={() => setzeMehrOffen(false)}>
                      {kopfhandlungen}
                    </div>
                  </div>
                ) : (
                  kopfhandlungen
                )}
                {handlung}
              </div>
            )}
          </div>
          {kennzahlen !== undefined && <div className="seitenkopf__kennzahlen">{kennzahlen}</div>}
          {reiter}
        </div>

        <div className={breit === true ? 'rumpf rumpf--breit' : 'rumpf'}>{children}</div>

        {/*
          DER SYSTEMSTAND IN DER FUSSZEILE (ABN-06). „Welcher Stand?" ist die
          erste Frage bei jeder Störung.
        */}
        <p className="fusszeile">Stand {import.meta.env.VITE_SYSTEMSTAND ?? 'unbekannt'}</p>
      </main>
    </div>
  );
}
